import React, { useContext, useEffect, useState } from "react";
import { DashboardContext } from "../../DashboardContext";
import { AppContext } from "../../../../appContext";
import { IFileProvider } from "../../../../api/fileProvider/IFileProvider";
import { MultiFileProvider } from "../../../../api/fileProvider/multiFileProvider";
import { AnalysisProvider } from "../../../../api/fileProvider/analysisProvider";
import { IAnalysisFolder } from "../fullAnalysis/IAnalysisFolder";
import { FullAnalysisFolder } from "../fullAnalysis/FullAnalysisFolder";
import { FullAnalysisFolderSettings } from "../fullAnalysis/FullAnalysisFolderSettings";
import { FullAnalysisCrumbs } from "../fullAnalysis/FullAnalysisCrumbs";
import { FullAnalysisFile } from "../fullAnalysis/FullAnalysisFile";
import { IPlanarFolder } from "../fullAnalysis/FSPlanarize";
import { IFullAnalysisFileRecord } from "../fullAnalysis/FullAnalysisTypes";
import { ConfirmationPopup } from "../../../../components/confirmationPopup/ConfirmationPopup";
import './AnalysisWidget.css';

// Helper to filter file records by client ID
const filterFileForClient = (file: IPlanarFolder, clientId: string): IPlanarFolder => {
    const result: IFullAnalysisFileRecord[] = [];
    if (file.fileData && Array.isArray(file.fileData)) {
        file.fileData.forEach(record => {
            if (record.relatedClientId == clientId) {
                result.push(record);
            }
        });
    }
    return {
        ...file,
        fileData: result
    };
};

export const AnalysisWidget = () => {
    const { selectedClient } = useContext(DashboardContext);
    const { translate } = useContext(AppContext);
    const [fileProvider, setFileProvider] = useState<IFileProvider | null>(null);
    const [selectedForEdit, setSelectedForEdit] = useState<IAnalysisFolder | null>(null);
    const [deleteConfirmItem, setDeleteConfirmItem] = useState<IAnalysisFolder | null>(null);
    const [selectedFileToView, setSelectedFileToView] = useState<IAnalysisFolder | null>(null);
    const [filterByClient, setFilterByClient] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [fix, setFix] = useState(0);

    // Initialize file provider - create and set in state first
    useEffect(() => {
        console.log('Analysis widget: Creating provider...');
        
        const provider = new MultiFileProvider(AnalysisProvider);
        provider.onChange = () => {
            console.log('Analysis widget: provider onChange triggered - files loaded');
            setIsLoading(false);
            setFix(last => last + 1);
        };
        provider.onAuthError = () => {
            console.error('Auth error in analysis widget');
            setIsLoading(false);
        };
        
        setFileProvider(provider);
    }, []);

    // Load files after provider is set in state
    useEffect(() => {
        if (!fileProvider) {
            return;
        }
        
        console.log('Analysis widget: Loading files from provider...');
        setIsLoading(true);
        
        fileProvider.getFiles()
            .catch(err => {
                console.error('Failed to load files:', err);
                setIsLoading(false);
            });
    }, [fileProvider]);

    const crumbs = fileProvider?.crumbs || [];
    const currentFolder = crumbs[crumbs.length - 1];
    const rawFolders = currentFolder?.folderItems || [];
    const showBreadcrumbs = crumbs.length > 1;

    console.log('Analysis widget: currentFolder=', currentFolder);
    console.log('Analysis widget: rawFolders count=', rawFolders.length);
    console.log('Analysis widget: filterByClient=', filterByClient);
    console.log('Analysis widget: isLoading=', isLoading);

    // Apply client filter only if checkbox is checked
    const folders = filterByClient && selectedClient?.id
        ? rawFolders.map(folder => {
            if (folder.type === 'file' && folder.fileData) {
                return filterFileForClient(folder as unknown as IPlanarFolder, selectedClient.id);
            }
            return folder;
        }).filter(folder => {
            if (folder.type === 'file' && folder.fileData && Array.isArray(folder.fileData)) {
                return folder.fileData.length > 0;
            }
            return true;
        })
        : rawFolders;

    const handleSelect = (folder: IAnalysisFolder | IPlanarFolder) => {
        if (folder.type === 'folder' && folder.id) {
            fileProvider?.getFiles(folder.id);
        } else if (folder.type === 'file') {
            // Open file in popup
            setSelectedFileToView(folder as IAnalysisFolder);
        }
    };

    const handleRename = (item: IAnalysisFolder, newName: string) => {
        if (!item.id) {
            console.error('Cannot rename item without ID');
            return;
        }
        fileProvider?.updateFile({ ...item, name: newName });
    };

    const handleDelete = (item: IAnalysisFolder) => {
        if (!item.id) {
            console.error('Cannot delete item without ID');
            return;
        }
        setDeleteConfirmItem(item);
    };

    const confirmDelete = () => {
        if (deleteConfirmItem?.id) {
            fileProvider?.deleteFile(deleteConfirmItem.id);
            setDeleteConfirmItem(null);
        }
    };

    const handleBreadcrumb = (folder: IAnalysisFolder) => {
        fileProvider?.getFiles(folder.id, folder.name);
    };

    const handleSettings = (item: IAnalysisFolder) => {
        setSelectedForEdit(item);
    };

    const handleAddFolder = () => {
        if (!fileProvider) {
            console.error('No file provider available - still loading');
            return;
        }

        const newFolder: IAnalysisFolder = {
            name: translate('analysis_new_folder') || 'New Folder',
            iconIndex: 0,
            date: new Date(),
            type: 'folder',
            folderItems: []
        };

        const parentId = currentFolder?.id || undefined;
        console.log('Creating folder with parentId:', parentId);
        fileProvider.createFolder(newFolder, parentId);
    };

    if (!selectedClient) {
        return (
            <div className="dashboard_main_block dashboard_main_analysis">
                <div className="dashboard_main_block_head">
                    <div className="dashboard_main_block_title">{translate('analysis_title')}</div>
                </div>
                <div className="analysis_widget_empty">
                    {translate('analysis_select_client') || 'Select a client to view analysis'}
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="dashboard_main_block dashboard_main_analysis">
                <div className="dashboard_main_block_head">
                    <div className="dashboard_main_block_title">
                        {translate('analysis_title')}
                    </div>
                </div>
                <div className="analysis_widget_loading">
                    {translate('common_loading')}
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard_main_block dashboard_main_analysis">
            <div className="dashboard_main_block_head">
                <div className="analysis_widget_title_wrapper">
                    <div className="dashboard_main_block_title">
                        {translate('analysis_title')}
                    </div>
                    <div 
                        className={`analysis_widget_filter_icon ${!filterByClient ? 'analysis_widget_filter_icon--active' : ''}`}
                        onClick={() => setFilterByClient(!filterByClient)}
                        title={filterByClient 
                            ? translate('filter_show_client_files').replace('{name}', selectedClient.name)
                            : translate('filter_show_all_files')
                        }
                    />
                </div>
                <div className="dashboard_main_block_head_right">
                    <div className="analysis_widget_add_wrapper" onClick={handleAddFolder}>
                        <div className="analysis_widget_add_icon"></div>
                        <span className="analysis_widget_add_text">{translate('analysis_add_folder') || 'Add a new folder'}</span>
                    </div>
                </div>
            </div>

            {showBreadcrumbs && (
                <div className="analysis_widget_breadcrumbs">
                    <FullAnalysisCrumbs
                        crumbs={crumbs}
                        onCrumb={handleBreadcrumb}
                    />
                </div>
            )}

            <div className="analysis_widget_content">
                <div className="dashboard_main_analysis_folders">
                    {folders.length === 0 && (
                        <div className="analysis_widget_empty_folder">
                            {translate('analysis_empty_folder') || 'No files or folders yet'}
                        </div>
                    )}
                    {folders.map((folder) => (
                        <FullAnalysisFolder
                            key={folder.id || folder.name}
                            folder={folder as IAnalysisFolder}
                            className=""
                            onSelect={() => handleSelect(folder)}
                            onRename={(newName) => handleRename(folder as IAnalysisFolder, newName)}
                            onDelete={(item) => handleDelete(item)}
                            onSettings={(item) => handleSettings(item)}
                        />
                    ))}
                </div>
            </div>

            {selectedForEdit && (
                <FullAnalysisFolderSettings
                    folder={selectedForEdit}
                    onClose={() => setSelectedForEdit(null)}
                    onSubmit={(data) => {
                        fileProvider?.updateFile({ ...selectedForEdit, ...data });
                        setSelectedForEdit(null);
                    }}
                />
            )}

            {deleteConfirmItem && (
                <ConfirmationPopup
                    message={`${translate('analysis_confirm_delete')} "${deleteConfirmItem.name}"?`}
                    confirmText={translate('common_delete') || 'Delete'}
                    cancelText={translate('common_cancel') || 'Cancel'}
                    onConfirm={confirmDelete}
                    onCancel={() => setDeleteConfirmItem(null)}
                    confirmButtonStyle="danger"
                />
            )}

            {selectedFileToView && (
                <div className="analysis_file_popup_overlay" onClick={() => setSelectedFileToView(null)}>
                    <div className="analysis_file_popup_content" onClick={(e) => e.stopPropagation()}>
                        <div className="analysis_file_popup_header">
                            <div className="analysis_file_popup_title">{selectedFileToView.name}</div>
                            <button className="analysis_file_popup_close" onClick={() => setSelectedFileToView(null)}>
                                ✕
                            </button>
                        </div>
                        <div className="analysis_file_popup_body">
                            <FullAnalysisFile file={selectedFileToView} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
